// Core
import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// Assets
import { apiService } from 'services/ApiService';

import { centsToPrice, priceToCents } from '../../../services/Utils';
import useFormPageRedirect from '../../../hooks/useFormPageRedirect';
import NextBasePriceContent from './components/NextBasePriceContent';

const basePriceSchema = z.object({
  code: z.string().min(1),
  price: z.coerce.number(),
  stationId: z.number().int().nullable().optional(),
});

const NextBasePriceEdit = () => {
  const history = useHistory();
  const { id } = useParams();

  const [errorCard, setErrorCard] = useState();

  const form = useForm({
    resolver: zodResolver(basePriceSchema),
    defaultValues: {
      code: '',
      price: 0,
    },
  });

  const { reset, handleSubmit } = form;

  const { canRead } = useFormPageRedirect({
    id,
    createPermissionKey: 'POST:/admin/next-base-prices',
    readPermissionKey: 'GET:/admin/next-base-prices/:id',
    redirectPath: '/next-base-prices',
  });

  const { isLoading, isError } = useQuery({
    queryKey: ['next-base-prices', id, canRead],
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnError: false,
    queryFn: async () => {
      if (id === 'new') {
        reset({
          code: '',
          price: 0,
        });
        return true;
      }
      if (canRead) {
        const response = await apiService.getNextBasePrice({ id });
        reset({
          ...response.data,
          price: priceToCents(response?.data?.price || 0),
        });
      }

      return true;
    },
  });

  const mutation = useMutation({
    mutationFn: async (data) => {
      const payload = { ...data, price: centsToPrice(data?.price || 0) };
      if (id === 'new') {
        return apiService.createNextBasePrice(payload);
      } else {
        return apiService.updateNextBasePrice({ id, ...payload });
      }
    },
    onSuccess: (res) => {
      if (
        res.data === false ||
        !(res.statusCode >= 200 && res.statusCode < 300)
      ) {
        setErrorCard(res.message);
        return;
      }
      history.push('/base-prices/next-base-prices');
    },
    onError: () => {
      setErrorCard('Something went wrong.');
    },
  });

  const onSubmit = (values) => {
    mutation.mutate(values);
  };

  const onError = (errors) => {
    console.log('Validation errors:', errors);

    const messages = Object.values(errors)
      .map((err) => err?.message)
      .filter(Boolean)
      .join('. ');

    setErrorCard(messages);
  };

  const handleDelete = useCallback(async () => {
    await apiService.deleteNextBasePrice({ id });
    reset({
      code: '',
      price: 0,
    });
    history.push('/base-prices/next-base-prices');
  }, [history, id, reset]);

  return isError ? (
    <div>Not Found</div>
  ) : isLoading ? (
    <div>Loading...</div>
  ) : (
    <NextBasePriceContent
      handleDelete={handleDelete}
      isNew={id === 'new'}
      form={form}
      onSubmit={handleSubmit(onSubmit, onError)}
      isSubmitting={mutation.isLoading}
      errorCard={errorCard}
    />
  );
};

export default NextBasePriceEdit;
